<template>
  <ModalWrapperComponent
    dialog-name="AutoRenewOptionComponent"
    :show-dialog="showDialog"
    @close-modal="close"
    @pre-open="beforeOpen"
  >
    <template #header>
      <div v-if="header">
        {{ tn(header) }}
      </div>
    </template>

    <template #content>
      <AutoRenewTurnOnComponent
        v-if="currentState === autoRenewOptionState.autoRenewTurnOn"
        :subscription="subscription"
        @change-payment-method="onChangePaymentMethod"
      />
      <AutoRenewTurnOffComponent
        v-if="currentState === autoRenewOptionState.autoRenewTurnOff"
        :subscription="subscription"
      />
      <AutoRenewOffSurveyComponent
        v-if="currentState === autoRenewOptionState.autoRenewTurnOffSurvey"
        @values="setSurveyValues"
      />
      <WebrootARTurnOnComponent v-if="currentState === autoRenewOptionState.webrootAutoRenewTurnOn" />
      <WebrootARTurnOffComponent
        v-if="currentState === autoRenewOptionState.webrootAutoRenewTurnOff"
        :subscription="subscription"
      />
    </template>

    <template #footer>
      <button
        v-if="cancelButtonText"
        id="btnCancelChanges"
        type="button"
        class="btn-link mr"
        :disabled="isAborting"
        @click="cancelModalOrSkipSurvey"
      >
        <spinner :is-spinning="isAborting" />
        {{ tn(cancelButtonText) }}
      </button>
      <button
        v-if="submitButtonText && isPromiseSubmit"
        id="btnSaveChanges"
        type="button"
        class="btn-primary"
        :disabled="submitDisabled || isSaving"
        @click="save"
      >
        <spinner :is-spinning="isSaving" />
        {{ tn(submitButtonText) }}
      </button>
      <button
        v-if="submitButtonText && !isPromiseSubmit"
        id="btnSaveChanges"
        type="button"
        class="btn-primary"
        :disabled="submitDisabled || isSaving"
        @click="save"
      >
        <spinner :is-spinning="isSaving" />
        {{ tn(submitButtonText) }}
      </button>
    </template>
  </ModalWrapperComponent>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { unifiedApi } from "@/common";
import { IAutoRenewUpdateRequest, ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { KeyCodeSeller } from "@/globalEnums";
import AutoRenewTurnOffComponent from "@/components/Subscriptions/AutoRenewTurnOff.vue";
import AutoRenewTurnOnComponent from "@/components/Subscriptions/AutoRenewTurnOn.vue";
import AutoRenewOffSurveyComponent from "@/components/Subscriptions/AutoRenewOffSurvey.vue";
import WebrootARTurnOnComponent from "@/components/Subscriptions/WebrootARTurnOn.vue";
import WebrootARTurnOffComponent from "@/components/Subscriptions/WebrootARTurnOff.vue";
import Spinner from "@/components/shared/Spinner.vue";
import ModalWrapperComponent from "@/components/shared/dialogs/ModalWrapper.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { getUserDetailsFromStore } from "@/common/userDetails";

const componentName = "AutoRenewOptionComponent";
//Reason Code 107 is used by MosE for when the survey is skipped
const turnOffAutoRenewSurveySkippedReasonCode = 107;

enum autoRenewOptionState {
  autoRenewTurnOn,
  autoRenewTurnOff,
  autoRenewTurnOffSurvey,
  webrootAutoRenewTurnOn,
  webrootAutoRenewTurnOff,
}

export default defineComponent({
  name: componentName,
  components: {
    AutoRenewTurnOffComponent,
    AutoRenewTurnOnComponent,
    AutoRenewOffSurveyComponent,
    WebrootARTurnOnComponent,
    WebrootARTurnOffComponent,
    ModalWrapperComponent,
    Spinner,
  },
  props: {
    subscription: {
      type: Object as PropType<ISubscription>,
      required: true,
    },
    showDialog: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["save-modal", "close-modal"],

  setup(props, context) {
    logEvent("created", componentName, props.subscription);

    const isSaving = ref<boolean>(false);
    const isAborting = ref<boolean>(false);
    const currentState = ref<autoRenewOptionState>();
    const header = ref<string>("");
    const cancelButtonText = ref<string>("");
    const submitButtonText = ref<string>("");
    const isPromiseSubmit = ref<boolean>(true);
    const surveySelectedReason = ref<number | null>(null);
    const surveyOtherReason = ref<string>("");
    const submitDisabled = ref<boolean>(false);

    const config = [
      {
        type: autoRenewOptionState.autoRenewTurnOn,
        header: "AutoRenewTurnOnHeader",
        cancelButtonText: "footerButtonCancel",
        submitButtonText: "footerButtonTurnOnAutoRenewal",
        isPromiseSubmit: true,
      },
      {
        type: autoRenewOptionState.autoRenewTurnOff,
        header: "AutoRenewTurnOffHeader",
        cancelButtonText: "footerButtonCancel",
        submitButtonText: "footerButtonTurnOffAutoRenewal",
        isPromiseSubmit: false,
      },
      {
        type: autoRenewOptionState.autoRenewTurnOffSurvey,
        header: "AutoRenewOffSurveyComponentHeader",
        cancelButtonText: "skipSurvey",
        submitButtonText: "submit",
        isPromiseSubmit: true,
      },
      {
        type: autoRenewOptionState.webrootAutoRenewTurnOn,
        header: "WebrootARTurnOnHeader",
        cancelButtonText: "",
        submitButtonText: "footerButtonClose",
        isPromiseSubmit: false,
      },
      {
        type: autoRenewOptionState.webrootAutoRenewTurnOff,
        header: "WebrootARTurnOffHeader",
        cancelButtonText: "",
        submitButtonText: "footerButtonClose",
        isPromiseSubmit: false,
      },
    ];

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    async function setAutoRenewModalType(subscription: ISubscription) {
      logEvent("setAutoRenewModalType", componentName, subscription.isAutoRenewEnabled);

      currentState.value = subscription.isAutoRenewEnabled
        ? props.subscription.keyCodeSellerType === KeyCodeSeller.Webroot
          ? autoRenewOptionState.webrootAutoRenewTurnOff
          : autoRenewOptionState.autoRenewTurnOff
        : props.subscription.keyCodeSellerType === KeyCodeSeller.Webroot
        ? autoRenewOptionState.webrootAutoRenewTurnOn
        : autoRenewOptionState.autoRenewTurnOn;

      await getCurrentConfig();
    }

    async function getCurrentConfig() {
      const currentConfig = config.find(c => c.type === currentState.value);
      if (currentConfig) {
        header.value = currentConfig.header;
        cancelButtonText.value = currentConfig.cancelButtonText;
        submitButtonText.value = currentConfig.submitButtonText;
        isPromiseSubmit.value = currentConfig.isPromiseSubmit;
      }
    }

    async function cancelModalOrSkipSurvey() {
      isAborting.value = true;
      if (currentState.value === autoRenewOptionState.autoRenewTurnOffSurvey) {
        surveySelectedReason.value = turnOffAutoRenewSurveySkippedReasonCode;
        await save();
      }
      isAborting.value = false;

      close();
    }

    async function save() {
      if (
        currentState.value === autoRenewOptionState.autoRenewTurnOn ||
        currentState.value === autoRenewOptionState.autoRenewTurnOffSurvey
      ) {
        isSaving.value = !isAborting.value;
        await onAutoRenewTurnOnOrOffSurvey(surveySelectedReason.value, surveyOtherReason.value);
        isSaving.value = false;
      } else if (currentState.value === autoRenewOptionState.autoRenewTurnOff) {
        currentState.value = autoRenewOptionState.autoRenewTurnOffSurvey;
        await getCurrentConfig();
        submitDisabled.value = true;
      } else if (
        currentState.value === autoRenewOptionState.webrootAutoRenewTurnOff ||
        currentState.value === autoRenewOptionState.webrootAutoRenewTurnOn
      ) {
        cancelModalOrSkipSurvey();
      }
    }

    function onChangePaymentMethod() {
      context.emit("save-modal", "CHANGE_PAYMENT_METHOD");
    }

    async function onAutoRenewTurnOnOrOffSurvey(surveyCode, surveyInput) {
      if (!props.subscription.paymentInfo?.cardType && !props.subscription.isAutoRenewEnabled) {
        context.emit("save-modal", "OK");
      } else {
        try {
          const updateAutoRenewOptionRequest: IAutoRenewUpdateRequest = {
            allowAutoRenew: !props.subscription.isAutoRenewEnabled,
            surveyChoice: surveyInput,
            zuoraSubscriptionNumber: props.subscription.zuoraSubscriptionNumber,
            surveyChoiceCode: surveyCode,
          };
          const updated = (await unifiedApi.updateAutoRenewOption(updateAutoRenewOptionRequest)).data;
          if (updated) {
            await getUserDetailsFromStore(true);
            context.emit("save-modal", "OK");
          } else {
            context.emit("save-modal", "FAIL");
          }
        } catch (error) {
          handleApiError(error as AxiosError);
        }
      }
    }

    function setSurveyValues(selectedReason, otherReason) {
      surveySelectedReason.value = selectedReason;
      surveyOtherReason.value = otherReason;
      submitDisabled.value =
        surveySelectedReason.value === 0 ||
        (surveySelectedReason.value === 106 && surveyOtherReason.value.length === 0);
    }

    async function beforeOpen() {
      await setAutoRenewModalType(props.subscription);
    }

    function close() {
      context.emit("close-modal");
    }

    //Expose to template
    return {
      tn,
      beforeOpen,
      close,
      setSurveyValues,
      save,
      cancelModalOrSkipSurvey,
      onChangePaymentMethod,
      submitDisabled,
      surveyOtherReason,
      surveySelectedReason,
      isPromiseSubmit,
      submitButtonText,
      cancelButtonText,
      header,
      currentState,
      autoRenewOptionState,
      isSaving,
      isAborting,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
